/**
 * yt-stop-sharing.js — controller for sections/yt-stop-sharing.liquid
 * (partner programme Phase 6, docs/12-partners/03 §3.3 withdrawal paths;
 * wording docs/12-partners/legal/client-consent-v1.md §4).
 *
 * The "Stop sharing" link in the footer of every email about a shared
 * subscription. The token rides in the URL FRAGMENT
 * (#t=<client id>.<expiry>.<signature>); we stash it in sessionStorage,
 * strip it from the address bar, then — POST only, token in the BODY:
 *
 *   POST /apps/yutrients/approvals/stop-sharing {token}
 *        → { partner_display_name, client_first_name, already_stopped }
 *   POST /apps/yutrients/approvals/stop-sharing {token, confirm:true}
 *        → { ok, stopped, already_stopped, cancelled_requests,
 *            partner_display_name }
 *        404 for an unknown, malformed or expired token (generic message).
 *
 * Nothing happens on load: the first call only reads. Withdrawal is
 * idempotent server-side, so a second confirm is harmless. The token
 * never appears in a link, the address bar or any tracking call; no
 * analytics fire from this file.
 */
(function () {
  'use strict';

  var PROXY = '/apps/yutrients';
  var TOKEN_KEY = 'yt_stop_token';
  var TOKEN_RE = /^[A-Za-z0-9._-]{20,200}$/;

  function clean(s) {
    return String(s == null ? '' : s).replace(/[\u0000-\u001f\u007f]+/g, ' ').trim();
  }
  async function safeJson(res) {
    try { return await res.json(); } catch (_e) { return null; }
  }
  function readToken() {
    var t = null;
    var m = /(?:^#|[#&])t=([A-Za-z0-9._-]+)(?:&|$)/.exec(location.hash || '');
    if (m) {
      t = m[1];
      try { sessionStorage.setItem(TOKEN_KEY, t); } catch (_e) {}
      try { history.replaceState(null, '', location.pathname + location.search); } catch (_e) {}
    }
    if (!t) {
      try { t = sessionStorage.getItem(TOKEN_KEY); } catch (_e) {}
    }
    return t && TOKEN_RE.test(t) ? t : null;
  }
  function post(body) {
    return fetch(PROXY + '/approvals/stop-sharing', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  }

  var root, panels;

  function fillSlots(panel, slots) {
    if (!panel || !slots) return;
    panel.querySelectorAll('[data-yta-slot]').forEach(function (el) {
      var k = el.getAttribute('data-yta-slot');
      if (Object.prototype.hasOwnProperty.call(slots, k)) el.textContent = slots[k];
    });
  }
  function setState(name, slots) {
    if (!panels[name]) name = 'invalid';
    root.setAttribute('data-yt-state', name);
    Object.keys(panels).forEach(function (k) { panels[k].hidden = k !== name; });
    fillSlots(panels[name], slots);
    if (name !== 'loading') {
      var h = panels[name].querySelector('h1');
      if (h) { h.setAttribute('tabindex', '-1'); try { h.focus({ preventScroll: true }); } catch (_e) {} }
    }
  }
  function showRetry(fn) {
    var btn = panels.error.querySelector('[data-yta-retry]');
    if (btn) { btn.onclick = fn; btn.disabled = false; }
    setState('error');
  }
  function showError(msg, retryFn) {
    var el = panels.confirm.querySelector('[data-yta-error]');
    if (!el) return;
    el.textContent = '';
    el.hidden = !msg;
    if (!msg) return;
    var p = document.createElement('p');
    p.textContent = msg;
    el.appendChild(p);
    if (retryFn) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'yta-btn yta-btn--ghost yta-btn--small';
      b.textContent = 'Try again';
      b.addEventListener('click', retryFn);
      el.appendChild(b);
    }
  }
  function cancelledWords(n) {
    n = Number(n) || 0;
    if (n <= 0) return '';
    return n === 1
      ? ' A suggested change that was waiting for your decision has been cancelled.'
      : ' ' + n + ' suggested changes that were waiting for your decision have been cancelled.';
  }

  async function load(token) {
    setState('loading');
    var res;
    try {
      res = await post({ token: token });
    } catch (_e) {
      return showRetry(function () { load(token); });
    }
    var data = await safeJson(res);
    if (res.status === 404 || res.status === 410) return setState('invalid');
    if (!res.ok || !data || typeof data !== 'object') return showRetry(function () { load(token); });
    var partner = clean(data.partner_display_name) || 'your practitioner';
    if (data.already_stopped) return setState('already', { partner: partner });
    setState('confirm', { partner: partner });
    bind(token, partner);
  }

  var bound = false;
  function bind(token, partner) {
    if (bound) return;
    bound = true;
    var confirmBtn = panels.confirm.querySelector('[data-yta-confirm]');
    var keepBtn = panels.confirm.querySelector('[data-yta-keep]');
    if (confirmBtn) confirmBtn.addEventListener('click', function () { confirm(token, partner); });
    if (keepBtn) keepBtn.addEventListener('click', function () { setState('kept', { partner: partner }); });
  }

  var busy = false;
  function setBusy(on) {
    busy = on;
    var confirmBtn = panels.confirm.querySelector('[data-yta-confirm]');
    var keepBtn = panels.confirm.querySelector('[data-yta-keep]');
    if (confirmBtn) { confirmBtn.disabled = on; confirmBtn.setAttribute('aria-busy', on ? 'true' : 'false'); confirmBtn.textContent = on ? 'Stopping…' : 'Stop sharing'; }
    if (keepBtn) keepBtn.disabled = on;
  }

  async function confirm(token, partner) {
    if (busy) return;
    showError('');
    setBusy(true);
    var res;
    try {
      res = await post({ token: token, confirm: true });
    } catch (_e) {
      setBusy(false);
      return showError('We couldn’t reach Yutrients. Check your connection and try again — nothing has changed.', function () { confirm(token, partner); });
    }
    var data = await safeJson(res);
    if (res.status === 404 || res.status === 410) { setBusy(false); return setState('invalid'); }
    if (!res.ok || !data || !data.ok) {
      setBusy(false);
      if (res.status === 429) return showError('Too many attempts just now. Wait a minute and try again.');
      return showError('Something went wrong. Please try again — nothing has changed.', function () { confirm(token, partner); });
    }
    var who = clean(data.partner_display_name) || partner;
    if (data.already_stopped && !data.cancelled_requests) return setState('already', { partner: who });
    setState('stopped', { partner: who, cancelled: cancelledWords(data.cancelled_requests) });
  }

  function boot() {
    root = document.querySelector('[data-yt-stop-sharing]');
    if (!root) return;
    panels = {};
    root.querySelectorAll('[data-yta-state]').forEach(function (el) { panels[el.getAttribute('data-yta-state')] = el; });
    var token = readToken();
    if (!token) return setState('invalid');
    load(token);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
